import { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  CircleMarker,
  MapContainer,
  Polyline,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  RoutePoint,
  TruckRouteFuelResult,
} from "../../../interfaces/Operations/truckRouteFuel";
import {
  buildDriverFuelMessage,
  fuelDate,
  fuelDuration,
  fuelMoney,
} from "./truckRouteFuelMessage";

function RouteBounds({ points }: { points: RoutePoint[] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length > 1)
      map.fitBounds(
        points.map(
          (point) => [point.latitude, point.longitude] as [number, number],
        ),
        { padding: [30, 30] },
      );
  }, [map, points]);
  return null;
}
const gallons = (value: number) => `${value.toFixed(2)} US gal`;
const priceLabels: Record<string, string> = {
  missing: "Price unavailable",
  current: "Current report",
  unknownTime: "Time unknown",
  future: "Invalid future timestamp",
  stale: "Stale price",
};

export default function TruckRouteFuelResults({
  result,
}: {
  result: TruckRouteFuelResult;
}) {
  const [selectedId, setSelectedId] = useState(result.routes[0]?.id);
  const [copied, setCopied] = useState("");
  useEffect(() => {
    setSelectedId(result.routes[0]?.id);
    setCopied("");
  }, [result]);
  const selected =
    result.routes.find((route) => route.id === selectedId) ?? result.routes[0];
  const message = useMemo(
    () => (selected ? buildDriverFuelMessage(result, selected) : ""),
    [result, selected],
  );
  if (!selected)
    return (
      <Alert severity="warning">No usable truck routes were returned.</Alert>
    );
  const plan = selected.fuelPlan;
  const points = plan?.route.length ? plan.route : selected.route;
  const purchases = new Map(
    plan?.stops.map((stop) => [stop.stationId, stop]) ?? [],
  );
  const stationPositions = selected.stations.map(
    (station) => [station.latitude, station.longitude] as [number, number],
  );
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied("Driver message copied. Paste it into your messaging app.");
    } catch {
      setCopied("Clipboard unavailable. Select and copy the message below.");
    }
  };
  return (
    <Stack spacing={2}>
      <Typography variant="h6">Available truck routes</Typography>
      <Typography variant="body2" color="text.secondary">
        {result.routeCoverage}
      </Typography>
      {result.missingInputs.length > 0 && (
        <Alert severity="info">
          Enter {result.missingInputs.join(", ")} to add purchase quantities and
          fuel costs. Routes and stations are shown below.
        </Alert>
      )}
      <Box>
        <Grid container spacing={2}>
          {result.routes.map((route) => {
            const routePlan = route.fuelPlan;
            return (
              <Grid item xs={12} md={6} lg={4} key={route.id}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    height: "100%",
                    borderColor:
                      route.id === selected.id ? "primary.main" : undefined,
                    borderWidth: route.id === selected.id ? 2 : 1,
                  }}
                >
                  <Stack spacing={1}>
                    <Typography fontWeight={700}>{route.name}</Typography>
                    {route.isRecommended && (
                      <Chip
                        color="success"
                        label="Recommended · lowest fuel cost within time allowance"
                        size="small"
                        sx={{
                          height: "auto",
                          "& .MuiChip-label": { whiteSpace: "normal", py: 0.5 },
                        }}
                      />
                    )}
                    <Typography>
                      {(
                        routePlan?.distanceMiles ?? route.distanceMiles
                      ).toFixed(1)}{" "}
                      mi ·{" "}
                      {fuelDuration(
                        routePlan?.journeyMinutes ?? route.travelTimeMinutes,
                      )}
                      {routePlan ? " with fuel stops" : " driving"}
                    </Typography>
                    {(routePlan?.roadNames.length
                      ? routePlan.roadNames
                      : route.roadNames
                    ).length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Via{" "}
                        {(routePlan?.roadNames.length
                          ? routePlan.roadNames
                          : route.roadNames
                        ).join(" → ")}
                      </Typography>
                    )}
                    {routePlan ? (
                      <>
                        <Typography fontWeight={600}>
                          Fuel purchases:{" "}
                          {fuelMoney(routePlan.fuelPurchaseCost)}
                        </Typography>
                        <Typography variant="body2">
                          {routePlan.stops.length} fuel stops · +
                          {Math.ceil(routePlan.additionalTravelTimeMinutes)} min
                          vs fastest verified fueled journey
                        </Typography>
                        {!route.withinTimeAllowance && (
                          <Chip
                            color="warning"
                            label="Outside extra-time allowance"
                            size="small"
                          />
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="warning.main">
                        Fuel plan unavailable
                      </Typography>
                    )}
                    <Typography variant="body2">
                      {route.stations.length} chain stations ·{" "}
                      {
                        route.stations.filter(
                          (station) => station.price != null,
                        ).length
                      }{" "}
                      reported prices
                    </Typography>
                    <Button
                      variant={
                        route.id === selected.id ? "contained" : "outlined"
                      }
                      onClick={() => {
                        setSelectedId(route.id);
                        setCopied("");
                      }}
                    >
                      {route.id === selected.id
                        ? "Selected route"
                        : "View route & stations"}
                    </Button>
                  </Stack>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>
      <Box
        sx={{
          height: { xs: 340, md: 450 },
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <MapContainer
          center={
            points.length
              ? [points[0].latitude, points[0].longitude]
              : [39, -98]
          }
          zoom={5}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          {result.routes
            .filter((route) => route.id !== selected.id)
            .map((route) => (
              <Polyline
                key={route.id}
                positions={(route.fuelPlan?.route.length
                  ? route.fuelPlan.route
                  : route.route
                ).map(
                  (point) =>
                    [point.latitude, point.longitude] as [number, number],
                )}
                pathOptions={{
                  color: route.isRecommended ? "#2e7d32" : "#7b8794",
                  weight: 3,
                  opacity: 0.5,
                }}
                eventHandlers={{ click: () => setSelectedId(route.id) }}
              />
            ))}
          <Polyline
            positions={points.map(
              (point) => [point.latitude, point.longitude] as [number, number],
            )}
            pathOptions={{
              color: selected.isRecommended ? "#2e7d32" : "#1976d2",
              weight: 5,
            }}
          />
          {selected.stations.map((station, index) => (
            <CircleMarker
              key={station.id}
              center={stationPositions[index]}
              radius={purchases.has(station.id) ? 8 : 5}
              pathOptions={{
                color: purchases.has(station.id)
                  ? "#ed6c02"
                  : station.price != null
                    ? "#1976d2"
                    : "#757575",
                fillOpacity: 0.9,
              }}
            >
              <Popup>
                <strong>{station.name}</strong>
                <br />
                {station.address}
                <br />
                {station.price != null
                  ? `${fuelMoney(station.price, station.currency ?? "Unknown currency")}/${station.priceUnit ?? "unknown unit"}`
                  : "Price unavailable"}
                <br />
                {priceLabels[station.priceStatus] ?? station.priceStatus}
                {purchases.has(station.id) && (
                  <>
                    <br />
                    Buy {gallons(purchases.get(station.id)!.quantityGallons)}
                  </>
                )}
              </Popup>
            </CircleMarker>
          ))}
          <RouteBounds points={points} />
        </MapContainer>
      </Box>
      <Typography variant="caption" color="text.secondary">
        Selected route highlighted · orange stations: recommended purchases ·
        blue: reported price · gray: no price. Click another route to compare
        it.
      </Typography>
      {selected.warnings.map((warning, index) => (
        <Alert severity="warning" key={index}>
          {warning}
        </Alert>
      ))}
      {plan && (
        <>
          <Typography variant="h6">Fuel plan for {selected.name}</Typography>
          <Grid container spacing={1.5}>
            {[
              [
                "Distance including fuel detours",
                `${plan.distanceMiles.toFixed(1)} mi`,
              ],
              [
                "Journey including fuel stops",
                fuelDuration(plan.journeyMinutes),
              ],
              ["Estimated arrival", fuelDate(plan.estimatedArrivalUtc)],
              ["Trip fuel consumption", gallons(plan.tripFuelGallons)],
              ["Fuel purchase cost", fuelMoney(plan.fuelPurchaseCost)],
              ["Fuel at destination", gallons(plan.remainingFuelGallons)],
            ].map(([label, value]) => (
              <Grid item xs={6} md={4} key={label}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography fontWeight={600}>{value}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
          {!plan.stops.length && (
            <Alert severity="success">
              No fuel purchase needed: starting fuel covers the trip and
              destination reserve.
            </Alert>
          )}
          {plan.stops.map((stop, index) => (
            <Paper variant="outlined" sx={{ p: 2 }} key={stop.stationId}>
              <Stack spacing={1}>
                <Typography fontWeight={700}>
                  {index + 1}. {stop.stationName} — {stop.address}
                </Typography>
                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                  <Chip
                    color="primary"
                    label={`Buy ${gallons(stop.quantityGallons)}`}
                  />
                  <Chip
                    label={`Arrival fuel: ${gallons(stop.arrivalFuelGallons)}`}
                  />
                  <Chip
                    label={`${fuelMoney(stop.pricePerGallon)}/US gal · ${fuelMoney(stop.purchaseCost)}`}
                  />
                  <Chip
                    label={`After purchase: ${gallons(stop.arrivalFuelGallons + stop.quantityGallons)}`}
                  />
                  {stop.isPartialFill && (
                    <Chip color="info" label="Partial fill" />
                  )}
                </Stack>
                <Typography variant="body2">{stop.explanation}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Estimated station arrival:{" "}
                  {fuelDate(stop.estimatedArrivalUtc)}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </>
      )}
      <Typography variant="h6">
        Stations on this route · lowest reported price first
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Currencies and units are grouped separately. Stale and missing prices
        stay visible. Station mileage and detours refer to the initial corridor;
        the fuel plan above uses routed station entrances.
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {[
                "Station / location",
                "Reported price",
                "Price details",
                "Route mile / detour",
                "Fuel recommendation",
              ].map((label) => (
                <TableCell key={label}>{label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {selected.stations.map((station) => {
              const purchase = purchases.get(station.id);
              return (
                <TableRow key={station.id}>
                  <TableCell>
                    <Typography fontWeight={600} variant="body2">
                      {station.name}
                    </Typography>
                    <Typography variant="caption">{station.address}</Typography>
                  </TableCell>
                  <TableCell>
                    {station.price == null
                      ? "Unavailable"
                      : `${fuelMoney(station.price, station.currency ?? "Unknown currency")}/${station.priceUnit ?? "unknown unit"}`}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {priceLabels[station.priceStatus] ?? station.priceStatus}{" "}
                      · {station.paymentType ?? "Payment terms unknown"}
                    </Typography>
                    <Typography variant="caption">
                      {station.priceTimestampUtc
                        ? fuelDate(station.priceTimestampUtc)
                        : "No observation time"}
                    </Typography>
                    {station.priceSourceUrl &&
                      /^https?:\/\//i.test(station.priceSourceUrl) && (
                        <Link
                          display="block"
                          variant="caption"
                          href={station.priceSourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Price source
                        </Link>
                      )}
                  </TableCell>
                  <TableCell>
                    {station.routeOffsetMiles.toFixed(1)} mi · ~
                    {station.detourMinutes} min detour
                  </TableCell>
                  <TableCell>
                    {purchase
                      ? `Buy ${gallons(purchase.quantityGallons)} · ${fuelMoney(purchase.purchaseCost)}`
                      : "Optional station"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {!selected.stations.length && (
        <Alert severity="warning">
          No supported chain stations were discovered on this route.
        </Alert>
      )}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            Driver message · selected route and alternatives
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1.5}>
            <Button
              startIcon={<ContentCopyIcon />}
              variant="outlined"
              onClick={copy}
            >
              Copy driver message
            </Button>
            {copied && <Alert severity="info">{copied}</Alert>}
            <TextField
              multiline
              fullWidth
              minRows={8}
              maxRows={22}
              value={message}
              label="Message to copy and paste"
              InputProps={{ readOnly: true }}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Estimates and coverage</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1}>
            {result.warnings.map((warning, index) => (
              <Typography variant="body2" key={index}>
                {warning}
              </Typography>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}
